import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Mail, Lock, AlertCircle, UserPlus, KeyRound, RefreshCw, Calculator, Image, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

// Helper to get captcha type icon
const getCaptchaIcon = (type) => {
  switch (type) {
    case 'image':
      return <Image className="w-4 h-4" />;
    case 'audio':
      return <Volume2 className="w-4 h-4" />;
    default:
      return <Calculator className="w-4 h-4" />;
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // CAPTCHA state
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(true);
  const [captchaType, setCaptchaType] = useState('text'); // 'text', 'image', or 'audio'
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRef = useRef(null);
  
  const { login, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  const fetchCaptcha = useCallback(async (type = captchaType) => {
    setLoadingCaptcha(true);
    setCaptchaAnswer('');
    setIsPlayingAudio(false);
    try {
      const response = await authAPI.getCaptcha(type);
      setCaptcha(response.data);
      setCaptchaType(response.data.type || 'text');
    } catch (err) {
      console.error('Failed to load CAPTCHA:', err);
      toast.error('Failed to load CAPTCHA. Please refresh.');
    } finally {
      setLoadingCaptcha(false);
    }
  }, [captchaType]);

  const toggleCaptchaType = (newType) => {
    fetchCaptcha(newType);
  };

  const playAudio = () => {
    if (audioRef.current && captcha?.audio) {
      audioRef.current.play();
      setIsPlayingAudio(true);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlayingAudio(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(isAdmin() ? '/dashboard' : '/employee-dashboard');
    } else {
      fetchCaptcha();
    }
  }, [isAuthenticated, navigate, isAdmin, fetchCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!captchaAnswer.trim()) {
      setError('Please solve the CAPTCHA');
      return;
    }
    
    setIsLoading(true);

    try {
      const result = await login({ 
        email, 
        password,
        captcha_challenge_id: captcha?.challenge_id,
        captcha_response: captchaAnswer.trim()
      });
      const userName = result?.user?.name || result?.user?.email || 'User';
      toast.success(`Welcome back, ${userName}!`);
      const userRole = result?.user?.role;
      navigate(userRole === 'admin' || userRole === 'hr' ? '/dashboard' : '/employee-dashboard');
    } catch (err) {
      setError(err.message);
      // Refresh CAPTCHA on failed login
      fetchCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-2xl text-slate-900">HRMS Solution</h1>
              <p className="text-sm text-slate-500">by Niyanta.io</p>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="font-heading text-3xl font-semibold text-slate-900 mb-2">Welcome back</h2>
            <p className="text-slate-600">Enter your credentials to access your account</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="login-error">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email or Login ID</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="text"
                  placeholder="email@company.com or login_id"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 h-12"
                  required
                  data-testid="email-input"
                />
              </div>
              <p className="text-xs text-slate-500">Enter your email address or login ID provided by admin</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 h-12"
                  required
                  data-testid="password-input"
                />
              </div>
              <div className="text-right">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-600 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {/* CAPTCHA Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 flex items-center gap-2">
                  {getCaptchaIcon(captchaType)}
                  Security Check
                </Label>
                <div className="flex gap-2 text-xs">
                  {captchaType !== 'text' && (
                    <button
                      type="button"
                      onClick={() => toggleCaptchaType('text')}
                      className="text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Text
                    </button>
                  )}
                  {captchaType !== 'image' && (
                    <button
                      type="button"
                      onClick={() => toggleCaptchaType('image')}
                      className="text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Image
                    </button>
                  )}
                  {captchaType !== 'audio' && (
                    <button
                      type="button"
                      onClick={() => toggleCaptchaType('audio')}
                      className="text-blue-600 hover:text-blue-700 hover:underline"
                      data-testid="audio-captcha-toggle"
                    >
                      Audio
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                {loadingCaptcha ? (
                  <div className="flex items-center justify-center py-2">
                    <div className="spinner w-5 h-5 border-slate-600 border-t-transparent"></div>
                    <span className="ml-2 text-sm text-slate-600">Loading...</span>
                  </div>
                ) : captchaType === 'audio' && captcha?.audio ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant={isPlayingAudio ? "destructive" : "default"}
                          size="sm"
                          onClick={isPlayingAudio ? stopAudio : playAudio}
                          className="flex items-center gap-2"
                          data-testid="play-audio-btn"
                        >
                          {isPlayingAudio ? (
                            <>
                              <VolumeX className="w-4 h-4" />
                              Stop
                            </>
                          ) : (
                            <>
                              <Volume2 className="w-4 h-4" />
                              Play Audio
                            </>
                          )}
                        </Button>
                        <audio 
                          ref={audioRef}
                          src={captcha.audio}
                          onEnded={() => setIsPlayingAudio(false)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchCaptcha('audio')}
                        className="text-slate-500 hover:text-slate-700"
                        data-testid="captcha-refresh-btn"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">
                      {captcha.instructions || 'Listen to the audio and type the 5-digit code'}
                    </p>
                    <Input
                      type="text"
                      placeholder="Enter the 5-digit code"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      className="h-10 font-mono tracking-widest text-center text-lg"
                      required
                      maxLength={5}
                      data-testid="captcha-input"
                    />
                  </>
                ) : captchaType === 'image' && captcha?.image ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <img 
                        src={captcha.image} 
                        alt="CAPTCHA" 
                        className="h-16 rounded border border-slate-200"
                        data-testid="captcha-image"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchCaptcha('image')}
                        className="text-slate-500 hover:text-slate-700"
                        data-testid="captcha-refresh-btn"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">
                      {captcha.instructions || 'Type the characters shown above'}
                    </p>
                    <Input
                      type="text"
                      placeholder="Enter the characters"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value.toUpperCase())}
                      className="h-10 font-mono uppercase tracking-wider"
                      required
                      maxLength={6}
                      data-testid="captcha-input"
                    />
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-lg font-medium text-slate-800" data-testid="captcha-question">
                        {captcha?.question || 'Loading...'}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchCaptcha('text')}
                        className="text-slate-500 hover:text-slate-700"
                        data-testid="captcha-refresh-btn"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                    <Input
                      type="text"
                      placeholder="Enter your answer"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      className="h-10"
                      required
                      data-testid="captcha-input"
                    />
                  </>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              disabled={isLoading || loadingCaptcha}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="spinner w-4 h-4 border-white border-t-transparent"></div>
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          {/* Register Link */}
          <div className="mt-8 pt-8 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-600">
              New employee?{' '}
              <Link 
                to="/register" 
                className="text-slate-900 font-medium hover:underline inline-flex items-center gap-1"
                data-testid="register-link"
              >
                <UserPlus className="w-4 h-4" />
                Register Now
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1759684137001-5e4a3aea4b53?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-slate-900/60"></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="text-2xl font-heading font-light leading-relaxed mb-4">
            "Streamline your HR processes with our comprehensive management system."
          </blockquote>
          <p className="text-slate-300">— HRMS Solution by Niyanta.io</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
